import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Phone, ShieldOff, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * FCC Oct 2026 compliance surface (per `FCC opt-out design notes.md`,
 * §6 — Website updates). Lets a recipient opt out of our outbound
 * calls/texts by category.
 *
 * Scope today (Phase 1): submit opts a request for review-and-process.
 * Full backend wiring to per-category consent records + Twilio
 * keyword routing lands in Phase 2 after counsel sign-off (§4
 * decision: exclusive opt-out method vs. "any reasonable means").
 *
 * We deliberately expose BOTH category toggles even on the lower
 * tier; a user may want to pre-opt-out of marketing before any
 * marketing SMS is ever sent to them. Opting out of a category we
 * don't actively send is a no-op but creates a valid audit record.
 */
function normalizePhone(raw) {
  // Loose E.164 normalization: strip anything that's not a digit, allow a
  // leading +. For US-only inputs we accept 10 digits and prepend +1.
  const trimmed = (raw || '').trim();
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/[^\d]/g, '');
  if (!digits) return null;
  if (hasPlus) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
}

export default function OptOut() {
  const [phone, setPhone] = useState('');
  const [stopReminders, setStopReminders] = useState(true);
  const [stopMarketing, setStopMarketing] = useState(true);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const e164 = normalizePhone(phone);
    if (!e164) {
      setError('Enter a valid US phone number (10 digits or E.164 format).');
      return;
    }
    if (!stopReminders && !stopMarketing) {
      setError('Select at least one category to opt out of.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/opt-out`, {
        phone: e164,
        categories: {
          appointment_reminders: stopReminders ? 'opted_out' : 'opted_in',
          marketing:             stopMarketing ? 'opted_out' : 'opted_in',
        },
        notes: notes.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail
        || 'We couldn\'t record your request. Try again, or email contact@listingaigent.com.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-brand-cream min-h-screen">
        <Hero />
        <section className="py-16">
          <div className="max-w-xl mx-auto px-6" data-testid="optout-success">
            <div className="bg-white rounded-2xl border border-brand-cream-dark p-10 text-center">
              <CheckCircle2 className="w-14 h-14 mx-auto text-brand-green mb-4" />
              <h2 className="font-serif text-2xl text-brand-green">Request received</h2>
              <p className="mt-3 text-brand-green/80 leading-relaxed">
                We've logged your opt-out request for{' '}
                <span className="font-mono text-sm">{normalizePhone(phone)}</span>.
                Processing takes up to <strong>24 hours</strong>, after which the
                selected categories will stop. You may still receive messages
                already in flight.
              </p>
              <p className="mt-5 text-sm text-brand-green/70">
                You can also stop texts immediately by replying{' '}
                <code className="bg-brand-cream-dark px-2 py-0.5 rounded">STOP</code>
                {' '}to any text we send you.
              </p>
              <div className="mt-8">
                <Link to="/" className="inline-block px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold">
                  Return home
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-brand-cream min-h-screen">
      <Hero />
      <section className="py-14">
        <div className="max-w-xl mx-auto px-5 sm:px-8">
          <form onSubmit={submit}
                data-testid="optout-form"
                className="bg-white rounded-2xl border border-brand-cream-dark p-8 md:p-10 space-y-6">

            <CategoryExplanation />

            <div>
              <label htmlFor="ot-phone" className="block text-sm text-brand-green/70 mb-2">
                Phone number
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-green/50 pointer-events-none" />
                <input
                  id="ot-phone"
                  data-testid="optout-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="w-full pl-11 pr-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green"
                />
              </div>
              <p className="mt-1 text-xs text-brand-green/60">
                US numbers only. Enter 10 digits or in +1 format.
              </p>
            </div>

            <fieldset className="space-y-3">
              <legend className="block text-sm text-brand-green/70 mb-1">
                What should we stop sending?
              </legend>

              <CategoryCheckbox
                testid="optout-reminders"
                checked={stopReminders}
                onChange={setStopReminders}
                title="Appointment reminders & scheduling"
                description="Showing-time changes, confirmations, booking updates. (Informational — stopping this does not stop marketing.)"
              />

              <CategoryCheckbox
                testid="optout-marketing"
                checked={stopMarketing}
                onChange={setStopMarketing}
                title="Marketing & lead-follow-up messages"
                description="Promotional messages about an agent's listings or services. Stopping this stops ALL telemarketing from this caller."
              />
            </fieldset>

            <div>
              <label htmlFor="ot-notes" className="block text-sm text-brand-green/70 mb-2">
                Notes (optional)
              </label>
              <textarea
                id="ot-notes"
                data-testid="optout-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Which realtor or listing were you contacted about? Any context helps us process this faster."
                className="w-full px-4 py-3 rounded-lg bg-brand-cream border border-brand-cream-dark focus:outline-none focus:ring-2 focus:ring-brand-green/40 text-brand-green text-sm"
              />
            </div>

            {error && (
              <div data-testid="optout-error"
                   className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="optout-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg bg-brand-green text-brand-cream font-semibold hover:scale-[1.01] transition-transform disabled:opacity-70"
            >
              {busy
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</>)
                : (<><ShieldOff className="w-4 h-4" /> Submit opt-out request</>)}
            </button>

            <OtherOptOutMethods />
          </form>
        </div>
      </section>
    </div>
  );
}

function Hero() {
  return (
    <section className="bg-brand-green text-brand-cream py-14 md:py-20">
      <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 text-center">
        <span className="text-xs uppercase tracking-[0.2em] text-brand-gold">Legal</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl leading-tight">
          Stop calls &amp; texts
        </h1>
        <p className="mt-4 text-brand-cream/75 max-w-xl mx-auto">
          Choose which types of communications you no longer want to receive. Processed within 24 hours.
        </p>
      </div>
    </section>
  );
}

function CategoryExplanation() {
  // Rendered in plain prose so it reads like a disclosure, not a UX tip.
  return (
    <div className="rounded-lg border border-brand-green/15 bg-brand-cream/40 p-4 text-xs text-brand-green/80 leading-relaxed">
      <p className="font-semibold text-brand-green">How this works</p>
      <p className="mt-1">
        Under the FCC's Oct 2026 rule change, you can opt out of our outbound
        calls and texts by <strong>category</strong>. Opting out of appointment
        reminders will not stop marketing, and vice versa — if you want both
        stopped, check both boxes below.
      </p>
    </div>
  );
}

function CategoryCheckbox({ testid, checked, onChange, title, description }) {
  return (
    <label className="flex gap-3 p-3 rounded-lg border border-brand-cream-dark hover:border-brand-green/40 cursor-pointer transition-colors">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-brand-green"
      />
      <div>
        <div className="text-sm font-semibold text-brand-green">{title}</div>
        <div className="mt-1 text-xs text-brand-green/70 leading-relaxed">{description}</div>
      </div>
    </label>
  );
}

function OtherOptOutMethods() {
  return (
    <div className="mt-2 pt-4 border-t border-brand-cream-dark text-xs text-brand-green/70 space-y-2">
      <p className="font-semibold text-brand-green">Other ways to opt out</p>
      <ul className="list-disc ml-5 space-y-1">
        <li>
          Reply{' '}
          <code className="bg-brand-cream-dark px-1.5 py-0.5 rounded">STOP</code>
          {' '}to any text we send. Reply{' '}
          <code className="bg-brand-cream-dark px-1.5 py-0.5 rounded">HELP</code>
          {' '}for assistance.
        </li>
        <li>
          Tell our AI receptionist on a live call — say
          {' '}&ldquo;stop calling me&rdquo; or &ldquo;take me off the list.&rdquo;
        </li>
        <li>
          Email{' '}
          <a href="mailto:contact@listingaigent.com" className="underline text-brand-green">
            contact@listingaigent.com
          </a>
          .
        </li>
      </ul>
      <p className="pt-2">
        See our{' '}
        <Link to="/privacy#calls-and-texts" className="underline text-brand-green">Privacy Policy</Link>
        {' '}and{' '}
        <Link to="/terms#s8" className="underline text-brand-green">SMS section of our Terms</Link>
        {' '}for full detail.
      </p>
    </div>
  );
}
